import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { registerCalibriFonts, setCalibriFont } from '../utils/fontLoader';

export interface ClientOutstanding {
  name: string;
  outstanding: number;
  clientType?: 'import' | 'export' | 'both';
}

export interface CompanyOutstanding {
  name: string;
  outstanding: number;
}

export interface GrossProfitEntry {
  clientName: string;
  sale: number;
  cost: number;
  grossProfit: number;
  deduction: number;
  netProfit: number;
}

// Generate Client-wise Outstanding PDF
export const generateClientOutstandingPDF = async (
  clientOutstandings: ClientOutstanding[]
): Promise<jsPDF> => {
  const doc = new jsPDF({ orientation: 'portrait' });

  // Register Calibri fonts
  await registerCalibriFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 5;
  let yPosition = 10; // Start higher since no logo

  // Add report title
  doc.setFontSize(16);
  setCalibriFont(doc, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Clientwise Outstanding Report', pageWidth / 2, yPosition, { align: 'center' });

  // Add date
  yPosition += 8;
  doc.setFontSize(10);
  setCalibriFont(doc, 'normal');
  doc.text(`As of: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });

  // Calculate total
  const totalOutstanding = clientOutstandings.reduce((sum, client) => sum + client.outstanding, 0);

  // Prepare table data
  const tableData = clientOutstandings.map(client => [
    client.name,
    (client.clientType ? client.clientType.charAt(0).toUpperCase() + client.clientType.slice(1) : '—'),
    `PKR ${Math.round(client.outstanding).toLocaleString()}`
  ]);

  // Add total row
  tableData.push([
    'Total Outstanding',
    '',
    `PKR ${Math.round(totalOutstanding).toLocaleString()}`
  ]);

  // Add table
  yPosition += 10;
  autoTable(doc, {
    startY: yPosition,
    head: [['Client Name', 'Client Type', 'Outstanding Amount']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [252, 180, 116],
      textColor: [0, 0, 0],
      fontSize: 11,
      font: 'calibri',
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 10,
      cellPadding: 2,
      font: 'calibri'
    },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'left' },
      2: { halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: function (data) {
      // Right-align the "Outstanding Amount" header
      if (data.section === 'head' && data.column.index === 2) {
        data.cell.styles.halign = 'right';
      }
      // Make outstanding amounts red (except for total row)
      if (data.section === 'body' && data.column.index === 2 && data.row.index !== tableData.length - 1) {
        data.cell.styles.textColor = [247, 16, 0]; // Dark red color
      }
      // Make total row bold and highlighted
      if (data.row.index === tableData.length - 1 && data.section === 'body') {
        data.cell.styles.fillColor = [252, 180, 116];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 11;
      }
    },
    margin: { left: margin, right: margin }
  });

  // Add footer note
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a system generated report.', margin, pageHeight - 10);

  return doc;
};

// Generate Company-wise Outstanding PDF
export const generateCompanyOutstandingPDF = async (
  companyOutstandings: CompanyOutstanding[]
): Promise<jsPDF> => {
  const doc = new jsPDF({ orientation: 'portrait' });

  // Register Calibri fonts
  await registerCalibriFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 5;
  let yPosition = 10; // Start higher since no logo

  // Add report title
  doc.setFontSize(16);
  setCalibriFont(doc, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Companywise Outstanding Report', pageWidth / 2, yPosition, { align: 'center' });

  // Add date
  yPosition += 8;
  doc.setFontSize(10);
  setCalibriFont(doc, 'normal');
  doc.text(`As of: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });

  // Calculate total
  const totalOutstanding = companyOutstandings.reduce((sum, company) => sum + company.outstanding, 0);

  // Prepare table data
  const tableData = companyOutstandings.map(company => [
    company.name,
    `PKR ${Math.round(company.outstanding).toLocaleString()}`
  ]);

  // Add total row
  tableData.push([
    'Total Outstanding',
    `PKR ${Math.round(totalOutstanding).toLocaleString()}`
  ]);

  // Add table
  yPosition += 10;
  autoTable(doc, {
    startY: yPosition,
    head: [['Company Name', 'Outstanding Amount']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [252, 180, 116],
      textColor: [0, 0, 0],
      fontSize: 11,
      font: 'calibri',
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 10,
      cellPadding: 2,
      font: 'calibri'
    },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: function (data) {
      // Right-align the "Outstanding Amount" header
      if (data.section === 'head' && data.column.index === 1) {
        data.cell.styles.halign = 'right';
      }
      // Make outstanding amounts red (except for total row)
      if (data.section === 'body' && data.column.index === 1 && data.row.index !== tableData.length - 1) {
        data.cell.styles.textColor = [247, 16, 0]; // Dark red color
      }
      // Make total row bold and highlighted
      if (data.row.index === tableData.length - 1 && data.section === 'body') {
        data.cell.styles.fillColor = [252, 180, 116];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 11;
      }
    },
    margin: { left: margin, right: margin }
  });

  // Add footer note
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a system generated report.', margin, pageHeight - 10);

  return doc;
};

// Generate Gross Profit Analysis PDF
export const generateGrossProfitPDF = async (
  grossProfitEntries: GrossProfitEntry[]
): Promise<jsPDF> => {
  const doc = new jsPDF({ orientation: 'portrait' });

  // Register Calibri fonts
  await registerCalibriFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 5;
  let yPosition = 10;

  // Add report title
  doc.setFontSize(16);
  setCalibriFont(doc, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Gross Profit Analysis Report', pageWidth / 2, yPosition, { align: 'center' });

  // Add date
  yPosition += 6;
  doc.setFontSize(10);
  setCalibriFont(doc, 'normal');
  doc.text(`As of: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });

  // Calculate totals
  const totalSale = grossProfitEntries.reduce((sum, entry) => sum + entry.sale, 0);
  const totalCost = grossProfitEntries.reduce((sum, entry) => sum + entry.cost, 0);
  const totalDeduction = grossProfitEntries.reduce((sum, entry) => sum + entry.deduction, 0);
  const totalGrossProfit = totalSale - totalCost;
  const totalNetProfit = totalGrossProfit - totalDeduction;

  // Prepare table data
  const tableData = grossProfitEntries.map(entry => [
    entry.clientName,
    `PKR ${Math.round(entry.sale).toLocaleString()}`,
    `PKR ${Math.round(entry.cost).toLocaleString()}`,
    `PKR ${Math.round(entry.grossProfit).toLocaleString()}`,
    `PKR ${Math.round(entry.deduction).toLocaleString()}`,
    `PKR ${Math.round(entry.netProfit).toLocaleString()}`
  ]);

  // Add total row
  tableData.push([
    'Total',
    `PKR ${Math.round(totalSale).toLocaleString()}`,
    `PKR ${Math.round(totalCost).toLocaleString()}`,
    `PKR ${Math.round(totalGrossProfit).toLocaleString()}`,
    `PKR ${Math.round(totalDeduction).toLocaleString()}`,
    `PKR ${Math.round(totalNetProfit).toLocaleString()}`
  ]);

  // Add table
  yPosition += 10;
  
  autoTable(doc, {
    startY: yPosition,
    head: [['Client', 'Sale', 'Cost', 'Gross Profit', 'Deduction', 'Net Profit']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [252, 180, 116],
      textColor: [0, 0, 0],
      fontSize: 10,
      font: 'calibri',
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 10,
      cellPadding: 2,
      font: 'calibri'
    },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right', fontStyle: 'bold' },
      2: { halign: 'right', fontStyle: 'bold' },
      3: { halign: 'right', fontStyle: 'bold' },
      4: { halign: 'right', fontStyle: 'bold' },
      5: { halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: function (data) {
      // Make header alignments match column alignments
      if (data.section === 'head') {
        if (data.column.index === 0) {
          data.cell.styles.halign = 'left'; // Client
        } else {
          data.cell.styles.halign = 'right'; // Sale, Cost, Profit
        }
      }
      
      const isLastRow = data.row.index === tableData.length - 1;
      
      // Make total row bold and highlighted
      if (isLastRow && data.section === 'body') {
        data.cell.styles.fillColor = [252, 180, 116];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 10;
      }
      
      // Color code profit column (not in total row)
      if (data.column.index === 3 && data.section === 'body' && !isLastRow) {
        const profitText = String(data.cell.text?.[0] || '');
        const profitValue = parseFloat(profitText.replace(/[^0-9.-]/g, ''));
        
        if (profitValue >= 0) {
          data.cell.styles.textColor = [0, 128, 0]; // Green for positive
        } else {
          data.cell.styles.textColor = [255, 0, 0]; // Red for negative
        }
      }
      
      // Color code sale column (green)
      if (data.column.index === 1 && data.section === 'body' && !isLastRow) {
        data.cell.styles.textColor = [0, 128, 0];
      }
      
      // Color code cost column (red)
      if (data.column.index === 2 && data.section === 'body' && !isLastRow) {
        data.cell.styles.textColor = [255, 0, 0];
      }
    },
    margin: { left: margin, right: margin }
  });

  // Add footer note
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a system generated report.', margin, pageHeight - 10);

  return doc;
};

// Function to open PDF in new tab
export const openPDFInNewTab = (pdf: jsPDF, filename: string = 'report.pdf'): void => {
  const pdfBlob = pdf.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const newWindow = window.open(pdfUrl, '_blank');
  if (!newWindow) {
    // Fallback: download the PDF if popup is blocked
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(pdfUrl);
  }
};

